import { ErrorMessages } from "@outschool/errors";
import { useTranslation } from "@outschool/localization";
import { createActivitySlug, createPathWithActivity } from "@outschool/routes";
import { getIdFromSlug } from "@outschool/routes/src";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import {
  ClassPageVisitHistory,
  ListingCardFragment,
  SimilarListingsMiniTiles
} from "@outschool/ui-components-website";
import { Loading } from "@outschool/ui-legacy-component-library";
import _ from "lodash";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";

import * as Env from "../../shared/Env";
import * as Routes from "../../shared/Routes";
import NotEnrolledView from "../components/detailPage/NotEnrolledView";
import SimpleLayout from "../components/layouts/SimpleLayout";
import {
  ActivityDetailPageProvider,
  useActivityDetailPageContext
} from "../hooks/ActivityDetailPage";
import { SearchFiltersContextProvider } from "../lib/useSearchFilters";
import NotFoundPage from "./NotFoundPage";

const similarListingsQuery = gql`
  query SimilarListingsUnpublished(
    $activitySlugId: String!
    $withRecentReviews: Boolean!
    $skipRating: Boolean!
  ) {
    similarListingsUnpublished(activitySlugId: $activitySlugId) {
      popularElasticsearch {
        ...ListingCard_listing
      }
    }
  }
  ${ListingCardFragment}
`;

function ActivityDetailPage() {
  const { currentUser, currentUserHasLoaded, isLoggedIn } = useSession();
  const location = useLocation();
  const history = useHistory();
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const { t } = useTranslation("client\\routes\\ActivityDetailPage");

  const {
    activity,
    error,
    loading: activityIsLoading
  } = useActivityDetailPageContext();

  const unpublished =
    error && _.endsWith(error.message, ErrorMessages.CLASS_NO_LONGER_AVAILABLE);
  const notFound = !activityIsLoading && !activity;
  const showDetails = !activityIsLoading && !!activity;
  const allProps = {
    loading: activityIsLoading,
    notFound,
    unpublished,
    showDetails
  };

  React.useEffect(() => {
    if (!activity) {
      return;
    }

    if (!hasLoaded) {
      ClassPageVisitHistory.pushToTop(activity.uid);
      setHasLoaded(true);
    }
  }, [activity, hasLoaded, location.query.sectionUid]);

  const activitySlug = location?.pathname?.split("/")[2];
  const activitySlugId = getIdFromSlug(activitySlug);
  const { data: similarListingsData, loading: similarListingsLoading } =
    useQueryWithPreviousData(similarListingsQuery, {
      skip: !unpublished,
      variables: {
        activitySlugId,
        skipRating: false,
        withRecentReviews: false
      }
    });

  // redirect /classes/ to /groups/
  React.useEffect(() => {
    if (activityIsLoading || !activity) {
      return;
    }
    if (
      (activity.isClub && history.location.pathname.startsWith("/classes/")) ||
      (!activity.isClub && history.location.pathname.startsWith("/groups/"))
    ) {
      history.replace(Routes.activityPath(activity));
    }

    const canonicalActivitySlug = createActivitySlug(
      activity.title,
      activity.slug_id
    );

    if (!isLoggedIn && activitySlug !== canonicalActivitySlug) {
      const canonicalActivityPath = createPathWithActivity(
        activity,
        null,
        location.query
      );
      history.replace(canonicalActivityPath);
    }
  }, [
    activityIsLoading,
    activity,
    history,
    location,
    isLoggedIn,
    activitySlug
  ]);

  if (activityIsLoading) {
    return (
      <SimpleLayout {...allProps} allowCategoriesNavbar>
        <Loading delay={0} />
      </SimpleLayout>
    );
  }
  // we don't generate CDPs for unpublished classes in SSR (SSR returns a 404)
  // so we handle that case here by recommending similar listings
  else if (unpublished) {
    const similarListings =
      similarListingsData?.similarListingsUnpublished?.popularElasticsearch;
    if (similarListings?.length) {
      return (
        <SimpleLayout {...allProps} allowCategoriesNavbar>
          <SimilarListingsMiniTiles
            activity={{ isPublished: false, uid: "" }}
            listings={similarListings}
            isFullPage
          />
        </SimpleLayout>
      );
    } else if (similarListingsLoading) {
      return (
        <SimpleLayout {...allProps} allowCategoriesNavbar>
          <Loading delay={0} />
        </SimpleLayout>
      );
    } else {
      return <NotFoundPage message={t("This class is no longer available")} />;
    }
  } else if (
    (Env.isDevelopment || Env.isReviewApp) &&
    !!currentUserHasLoaded &&
    !currentUser
  ) {
    /* eslint-disable i18next/no-literal-string */
    return (
      <SimpleLayout {...allProps}>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ padding: "20px", width: 500 }}>
            <h2>Hello, Fellow Outschool Engineer</h2>
            If you haven't already
            <br />
            <code>cd /lang-js/apps/server-rendered-website</code>
            <br />
            then run
            <br />
            <code>pnpm install && pnpm build</code>
            <br />
            You are currently viewing a logged-out class details page in the CSR
            app (<code>lang-js/apps/website</code>) in development.
            <br />
            End users will never see these changes as all logged-out class
            details pages are served by the SSR app (
            <code>lang-js/apps/server-rendered-website</code>).
            <br />
            Please make all changes to the logged-out class details pages in the
            SSR app (<code>lang-js/apps/server-rendered-website</code>). Run{" "}
            <code>pnpm dev</code> and check out your changes at{" "}
            <a href={`http://localhost:3030${window.location.pathname}`}>
              localhost:3030
            </a>
            .
            <br />
            If you're looking to edit the logged-in class details page, then
            just log in and continue to make your changes in this app (the CSR
            app, <code>lang-js/apps/website</code>).
          </div>
        </div>
      </SimpleLayout>
    );
    /* eslint-enable i18next/no-literal-string */
  } else if (notFound) {
    return <NotFoundPage />;
  }
  return (
    <NotEnrolledView
      // @ts-ignore TS(2322): Type '{ __typename?: "Activity" | undefined; upsel... Remove this comment to see the full error message
      activity={activity}
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      leader={activity.leader}
      showDetails={showDetails}
    />
  );
}

export default function ActivityDetailPageWrapped() {
  return (
    <ActivityDetailPageProvider>
      <SearchFiltersContextProvider>
        <ActivityDetailPage />
      </SearchFiltersContextProvider>
    </ActivityDetailPageProvider>
  );
}
